<template>
    <div v-if="installation">
        <vrm-header :installation="installation">
            <template v-slot:header-title>
                {{ installation.name }}
            </template>
        </vrm-header>
        <vrm-title>
            <template v-slot:page-title>
                <h1 class="vrm-page-title">
                    {{
                        $t('users.users_installation', {
                            i18nFallback: 'Users',
                            name: installation.name,
                        })
                    }}
                </h1>
            </template>
            <template v-slot:mobile-page-title>
                <h1 class="vrm-page-title">
                    {{ $t('users.users', { i18nFallback: 'Users' }) }}
                </h1>
            </template>
        </vrm-title>
        <div class="container">
            <!-- Users -->
            <vrm-scroll-fade>
                <vrm-foldable-table :items="sortedUsers" key-by="idUser">
                    <template v-slot:thead>
                        <tr>
                            <th>{{ $t('users.user', { i18nFallback: 'User' }) }}</th>
                            <th>{{ $t('users.email', { i18nFallback: 'Email' }) }}</th>
                            <th>
                                {{ $t('users.role', { i18nFallback: 'Role' }) }}
                                <span
                                    class="sg-vrm-icon_061_info-round-16px vrm-table__icon--link"
                                    @click="showRoleGuideModal"
                                ></span>
                            </th>
                            <th />
                        </tr>
                    </template>
                    <template v-slot:main="{ item: user }">
                        <vrm-table-cell-user data-cy="user-name" :user="user"></vrm-table-cell-user>
                        <vrm-table-cell-email
                            data-cy="user-email"
                            :email="user.email"
                        ></vrm-table-cell-email>
                        <td>
                            <vrm-dropdown
                                v-model="user.accessLevelOption"
                                data-cy="user-access-level"
                                :options="accessLevelDropdownOptions"
                                :disabled="!canChangeUserAccess(user)"
                                :append-to-body="true"
                                :dropdown-classes="'ui-select--simple'"
                            />
                        </td>
                        <td class="vrm-table__actions">
                            <a
                                v-if="canUnlinkUser(user)"
                                class="sg-vrm-icon_055_cross-16px vrm-table__icon--link"
                                @click.prevent="unlinkUser(user.idUser)"
                            />
                            <i
                                v-else-if="canSeeTooltip(user)"
                                v-tooltip.bottom="
                                    $t('users.last_user', {
                                        i18nFallback:
                                            'There must always be at least one user with full control. You cannot delete this user',
                                    })
                                "
                                class="vrm-table__icon--link sg-vrm-icon_061_info-round-16px"
                            />
                        </td>
                    </template>
                    <template v-slot:noEntries>
                        {{
                            $t('users.no_users', {
                                i18nFallback:
                                    'No users were found for this installation. You can invite a new user by clicking on the "Invite user" button in the "Pending invitations" section.',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
            <!-- Access requests -->
            <template v-if="canMakeChanges && pendingUsers.length">
                <vrm-scroll-fade>
                    <vrm-foldable-table
                        :items="pendingUsers"
                        key-by="idUser"
                        key-unique-string="pendingUser"
                    >
                        <template v-slot:thead>
                            <tr>
                                <th>{{ $t('users.pending_user', { i18nFallback: 'User' }) }}</th>
                                <th>{{ $t('users.email', { i18nFallback: 'Email' }) }}</th>
                                <th>
                                    {{ $t('users.role', { i18nFallback: 'Role' }) }}
                                    <span
                                        class="sg-vrm-icon_061_info-round-16px vrm-table__icon--link"
                                        @click="showRoleGuideModal"
                                    ></span>
                                </th>
                                <th />
                            </tr>
                        </template>
                        <template v-slot:main="{ item: user }">
                            <vrm-table-cell-user :user="user"></vrm-table-cell-user>
                            <vrm-table-cell-email :email="user.email"></vrm-table-cell-email>
                            <td>
                                <vrm-dropdown
                                    v-model="user.accessLevelOption"
                                    :options="accessLevelDropdownOptions"
                                    :append-to-body="true"
                                    :dropdown-classes="'ui-select--simple'"
                                />
                            </td>
                            <td class="vrm-table__actions">
                                <a
                                    class="sg-btn sg-orange"
                                    @click.prevent="answerPending(user, false)"
                                >
                                    {{ $t('common.deny', { i18nFallback: 'Deny' }) }}
                                </a>
                                <a
                                    class="sg-btn sg-blue"
                                    @click.prevent="answerPending(user, true)"
                                >
                                    {{ $t('common.approve', { i18nFallback: 'Approve' }) }}
                                </a>
                            </td>
                        </template>
                    </vrm-foldable-table>
                </vrm-scroll-fade>
            </template>
            <!-- Invites -->
            <h2>
                {{ $t('users.invitations', { i18nFallback: 'Pending invitations' }) }}
            </h2>
            <vrm-scroll-fade>
                <vrm-foldable-table
                    :items="invitedUsers"
                    key-by="email"
                    key-unique-string="invitedUser"
                >
                    <template v-slot:thead>
                        <tr>
                            <th>
                                {{ $t('users.invited_user', { i18nFallback: 'Invited user' }) }}
                            </th>
                            <th>{{ $t('users.email', { i18nFallback: 'Email' }) }}</th>
                            <th>
                                {{ $t('users.role', { i18nFallback: 'Role' }) }}
                                <span
                                    class="sg-vrm-icon_061_info-round-16px vrm-table__icon--link"
                                    @click="showRoleGuideModal"
                                ></span>
                            </th>
                            <th />
                        </tr>
                    </template>
                    <template v-slot:main="{ item: user }">
                        <vrm-table-cell-user :user="user"></vrm-table-cell-user>
                        <vrm-table-cell-email :email="user.email"></vrm-table-cell-email>
                        <td>
                            <vrm-dropdown
                                v-model="user.accessLevelOption"
                                :options="accessLevelDropdownOptions"
                                :disabled="!canMakeChanges"
                                :append-to-body="true"
                                :dropdown-classes="'ui-select--simple'"
                            />
                        </td>
                        <td class="vrm-table__actions">
                            <a
                                v-if="canMakeChanges"
                                class="vrm-table__icon--link sg-vrm-icon_055_cross-16px"
                                @click.prevent="removeInvite(user.email)"
                            />
                        </td>
                    </template>
                    <template v-slot:noEntries>
                        {{
                            $t('users.no_pending_installation', {
                                i18nFallback:
                                    'No pending invites were found for this installation. You can invite a new user by clicking on the "Invite user" button.',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
            <div class="sg-btn__container--align-right">
                <a class="sg-btn sg-blue" @click.prevent="showInviteUserPopup">
                    {{ $t('users.invite', { i18nFallback: 'Invite user' }) }}
                </a>
            </div>

            <!-- Teams -->
            <h2>
                {{
                    $t('users.teams_installation', {
                        i18nFallback: 'Teams',
                        name: installation.name,
                    })
                }}
            </h2>
            <vrm-scroll-fade>
                <vrm-foldable-table :items="userGroups" key-by="idUserGroup">
                    <template v-slot:thead>
                        <tr>
                            <th>{{ $t('users.teams', { i18nFallback: 'Team name' }) }}</th>
                            <th>{{ $t('teams.admin', { i18nFallback: 'Admin' }) }}</th>
                            <th v-if="canSeeGroupAndTeamMembers">
                                {{ $t('users.members', { i18nFallback: 'Members' }) }}
                            </th>
                            <th>
                                {{ $t('users.role', { i18nFallback: 'Role' }) }}
                                <span
                                    class="sg-vrm-icon_061_info-round-16px vrm-table__icon--link"
                                    @click="showRoleGuideModal"
                                ></span>
                            </th>
                            <th v-if="canSeeGroupAndTeamMembers" />
                        </tr>
                    </template>
                    <template v-slot:main="{ item: userGroup, toggle, expanded }">
                        <vrm-table-cell-text :text="userGroup.name"></vrm-table-cell-text>
                        <vrm-table-cell-text :text="userGroup.adminName"></vrm-table-cell-text>
                        <td v-if="canSeeGroupAndTeamMembers" class="vrm-table__nowrap">
                            <vrm-avatar-stack :users="userGroup.users" admin-key="isAdmin" />
                        </td>
                        <td>
                            <vrm-dropdown
                                v-model="userGroup.accessLevelOption"
                                :options="accessLevelDropdownOptions"
                                :disabled="!canMakeChanges"
                                :append-to-body="true"
                                :dropdown-classes="'ui-select--simple'"
                            />
                        </td>
                        <td v-if="canSeeGroupAndTeamMembers" class="vrm-table__actions">
                            <a
                                class="vrm-table__icon--link"
                                :class="
                                    expanded
                                        ? 'sg-vrm-icon_059_min-round-16px'
                                        : 'sg-vrm-icon_058_plus-round-16px'
                                "
                                @click="toggle"
                            />
                        </td>
                    </template>
                    <template v-slot:extra="{ item: userGroup }">
                        <td colspan="999">
                            <div v-if="!hasVisibleTeamMembers(userGroup)"></div>

                            <vrm-foldable-table
                                v-if="hasVisibleTeamMembers(userGroup)"
                                :items="userGroup.users"
                                key-by="idUser"
                            >
                                <template v-slot:thead>
                                    <th>{{ $t('users.members') }}</th>
                                    <th>{{ $t('users.email') }}</th>
                                    <th />
                                </template>
                                <template v-slot:main="{ item: member }">
                                    <vrm-table-cell-user
                                        :user="member"
                                        admin-key="isAdmin"
                                    ></vrm-table-cell-user>
                                    <vrm-table-cell-email
                                        :email="member.email"
                                    ></vrm-table-cell-email>
                                </template>
                                <template v-slot:noEntries>
                                    {{
                                        $t('users.no_members', {
                                            i18nFallback:
                                                'No members/users were found for this team. You can add a new member to this team by going to the "Groups > Teams". Then you can either edit an existing team or create a new team and add member to it.',
                                        })
                                    }}
                                </template>
                            </vrm-foldable-table>

                            <div class="sg-spread">
                                <button
                                    v-if="canMakeChanges"
                                    type="button"
                                    class="sg-btn sg-site"
                                    @click.prevent="unlinkTeam(userGroup.idUserGroup)"
                                >
                                    {{ $t('users.team_unlink') }}
                                </button>
                            </div>
                        </td>
                    </template>
                    <template v-slot:noEntries>
                        {{
                            $t('users.no_teams', {
                                i18nFallback:
                                    'No teams were found for this installation. You can add a new team by clicking on the "Add Teams" button.',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>

            <div v-if="canMakeChanges" class="sg-btn__container--align-right">
                <a class="sg-btn sg-blue" @click.prevent="showLinkUserGroupsPopup">
                    {{ $t('users.add_team', { i18nFallback: 'Add team' }) }}
                </a>
            </div>

            <!-- Site groups -->
            <h2>
                {{
                    $t('users.site_groups_of_installation', {
                        i18nFallback: 'Installation groups with {name}',
                        name: installation.name,
                    })
                }}
            </h2>
            <vrm-scroll-fade>
                <vrm-foldable-table :items="siteGroups" key-by="idSiteGroup">
                    <template v-slot:thead>
                        <tr>
                            <th>
                                {{ $t('users.site_group', { i18nFallback: 'Installation group' }) }}
                            </th>
                            <th>{{ $t('teams.admin', { i18nFallback: 'Admin' }) }}</th>
                        </tr>
                    </template>
                    <template v-slot:main="{ item: siteGroup, toggle, expanded }">
                        <td>
                            {{ siteGroup.name }}
                        </td>
                        <td>
                            {{ siteGroup.adminName }}
                        </td>
                        <td v-if="canSeeGroupAndTeamMembers" class="vrm-table__actions">
                            <a
                                class="vrm-table__icon--link"
                                :class="
                                    expanded
                                        ? 'sg-vrm-icon_059_min-round-16px'
                                        : 'sg-vrm-icon_058_plus-round-16px'
                                "
                                @click="toggle"
                            />
                        </td>
                    </template>
                    <template v-slot:extra="{ item: siteGroup }">
                        <td colspan="999">
                            <vrm-table-users-and-teams
                                :users-and-teams="siteGroup.membersAndTeams"
                                :show-role-guide-modal="showRoleGuideModal"
                            />
                            <div class="sg-spread">
                                <button
                                    v-if="canMakeChanges && siteGroup.isOwnerOrAdmin"
                                    type="button"
                                    class="sg-btn sg-site"
                                    @click.prevent="unlinkInstallationGroup(siteGroup.idSiteGroup)"
                                >
                                    {{
                                        $t('users.site_group_unlink', {
                                            i18nFallback: 'Unlink installation group',
                                        })
                                    }}
                                </button>
                            </div>
                        </td>
                    </template>
                    <template v-slot:noEntries>
                        {{
                            $t('users.no_groups', {
                                i18nFallback:
                                    'No groups were found for this installation. You can add a new group to this installation by going to the "Groups > Installation groups". Then you can either edit an existing group or create a new group and add this installation to it.',
                            })
                        }}
                    </template>
                </vrm-foldable-table>
            </vrm-scroll-fade>
        </div>
    </div>
</template>
<script>
import { Vue, Component, Watch } from 'vue-property-decorator';
import { AngularRoute, ngInject } from '@/vue/decorators/angular-interop';
import VrmHeader from '@/vue/components/layout/VrmHeader';
import VrmTitle from '@/vue/components/layout/VrmTitle';
import VrmTableCellUser from '@/vue/components/table/VrmTableCellUser';
import VrmTableCellEmail from '@/vue/components/table/VrmTableCellEmail';
import VrmTableCellText from '@/vue/components/table/VrmTableCellText';
import VrmTableUsersAndTeams from '@/vue/components/table/VrmTableUsersAndTeams';
import VrmControlledToggle from '@/vue/components/shared/toggles/VrmControlledToggle';
import VrmAvatarStack from '@/vue/components/shared/VrmAvatarStack';
import { NOTIFICATION_TYPE_SUCCESS } from '@/vue/store/modules/notifications';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import InstallationLinkUserGroupsModal from '@/vue/components/modals/InstallationLinkUserGroupsModal';
import InstallationUserInviteModal from '@/vue/components/modals/InstallationUserInviteModal';
import ConfirmDeletionModal from '@/vue/components/modals/ConfirmDeletionModal';
import VrmFoldableTable from '@/vue/components/molecules/VrmFoldableTable';
import VrmScrollFade from '../../../shared/VrmScrollFade.vue';
import UserRolesGuideModal from '@/vue/components/modals/UserRolesGuideModal';
import VrmDropdown from '@/vue/components/shared/VrmDropdown';
import {
    accessLevelDropdownOptions,
    findAccessLevelOption,
    accessLevelDropdownOptionsFiltered,
} from '@/vue/store/modules/installation-access-level';

@Component({
    components: {
        VrmTableCellUser,
        VrmTableCellEmail,
        VrmTableCellText,
        VrmFoldableTable,
        VrmAvatarStack,
        VrmDropdown,
        VrmControlledToggle,
        VrmHeader,
        VrmScrollFade,
        VrmTitle,
        VrmTableUsersAndTeams,
    },
})
@AngularRoute('base.installation.settings.users', '/users', { slot: 'settings' })
@AngularLocaleBundle('core')
@AngularLocaleBundle('users')
@AngularLocaleBundle('teams')
export default class InstallationUsers extends Vue {
    @ngInject()
    ApiService;
    @ngInject()
    InstallationsService;
    @ngInject()
    vrmInstallationAccessLevel;
    @ngInject()
    vrmNotificationsService;
    @ngInject()
    UserGroupsService;
    @ngInject()
    locale;
    @ngInject()
    VrmModalService;
    @ngInject()
    $rootScope;
    @ngInject()
    UserService;
    @ngInject()
    $timeout;
    @ngInject()
    vrmUserAccessLevel;
    @ngInject()
    InstallationGroupsService;

    accessLevelDropdownOptions = accessLevelDropdownOptions;

    get canSeeGroupAndTeamMembers() {
        return (
            this.installation && this.installation.view_permissions.can_see_group_and_team_members
        );
    }

    get canMakeChanges() {
        return (
            (this.installation && this.installation.view_permissions.canAlterInstallation) ||
            this.$rootScope.userState.isFullAdmin
        );
    }

    get hasMoreThanOneAdmin() {
        let firstAdminFound = false;
        for (let user of this.users) {
            if (user.fullControl) {
                if (firstAdminFound) {
                    return true;
                } else {
                    firstAdminFound = true;
                }
            }
        }
        return false;
    }

    hasVisibleTeamMembers(userGroup) {
        return userGroup.users && Array.isArray(userGroup.users);
    }

    isTheOnlyFullAdmin(user) {
        return !this.hasMoreThanOneAdmin && user.fullControl;
    }

    canChangeUserAccess(user) {
        return this.canMakeChanges && !this.isTheOnlyFullAdmin(user);
    }

    canUnlinkUser(user) {
        return (
            (user.idUser === this.UserService.user.id &&
                (this.hasMoreThanOneAdmin || !user.fullControl)) ||
            this.canChangeUserAccess(user)
        );
    }

    canSeeTooltip(user) {
        return user.idUser === this.UserService.user.id || this.canMakeChanges;
    }

    get idSite() {
        return this.$route.params.id;
    }

    get localUserId() {
        const user = this.$store.state.UserService.user;
        return user ? user.id : 0;
    }

    get sortedUsers() {
        return this.users.sort((userA, userB) => userA.name.localeCompare(userB.name));
    }

    installation = null;
    users = [];
    pendingUsers = [];
    invitedUsers = [];
    userGroups = [];
    siteGroups = [];

    @Watch('idSite', { immediate: true })
    async loadSiteData() {
        if (!this.idSite) return;
        const idBefore = this.idSite;
        const installation = await this.InstallationsService.getInstallation(this.idSite);
        if (idBefore !== this.idSite) return;
        this.installation = installation;
    }

    @Watch('idSite', { immediate: true })
    async loadListData() {
        if (!this.idSite) return;
        const idBefore = this.idSite;
        const { data } = await this.ApiService.call2(`/installations/${this.idSite}/users`);
        await this.InstallationGroupsService.loadInstallationGroups();
        if (idBefore !== this.idSite) return;
        const self = this;
        // We add a fullControl property that wraps some logic for the toggles
        this.users = data.users.map(user => ({
            ...user,
            get fullControl() {
                return this.accessLevel === self.vrmInstallationAccessLevel.FULL_CONTROL;
            },
            get accessLevelOption() {
                return findAccessLevelOption(this.accessLevel);
            },
            set accessLevelOption(option) {
                const newAccessLevel = option.accessLevel;
                const adminIsDowngradingSelf =
                    this.fullControl &&
                    this.idUser === self.UserService.user.id &&
                    newAccessLevel !== self.vrmInstallationAccessLevel.FULL_CONTROL;

                if (adminIsDowngradingSelf) {
                    // Update the value of accessLevel to trigger the dropdown to show the correct value
                    // and then revert it back to the original value
                    // This is needed because the dropdown does not update the selected value if the model is not changed
                    const currentAccessLevel = this.accessLevel;
                    this.accessLevel = newAccessLevel;
                    self.$timeout(() => {
                        this.accessLevel = currentAccessLevel;
                    });

                    self.VrmModalService.openConfirmModal(
                        self.$rootScope,
                        'downgrade_access',
                        confirmed => {
                            if (confirmed) {
                                this.changeAccessLevel(newAccessLevel);
                            }
                        },
                        {
                            yesBtnClass: 'sg-red',
                            noTranslation: 'common.cancel',
                        },
                    );
                } else {
                    this.changeAccessLevel(newAccessLevel);
                }
            },
            async changeAccessLevel(value) {
                await self.updateUserAccessLevel(this.idUser, value);
                this.accessLevel = value;
            },
        }));
        this.pendingUsers = data.pending.map(user => ({
            ...user,
            get fullControl() {
                return this.accessLevel === self.vrmInstallationAccessLevel.FULL_CONTROL;
            },
            get accessLevelOption() {
                return findAccessLevelOption(this.accessLevel);
            },
            set accessLevelOption(option) {
                this.accessLevel = option.accessLevel;
            },
        }));
        this.invitedUsers = data.invites.map(invite => ({
            ...invite,
            get accessLevelOption() {
                return findAccessLevelOption(this.accessLevel);
            },
            set accessLevelOption(option) {
                self.updateInviteAccessLevel(this.email, option.accessLevel);
                this.accessLevel = option.accessLevel;
            },
        }));
        this.userGroups = data.userGroups.map(userGroup => ({
            ...userGroup,
            get fullControl() {
                return this.accessLevel === self.vrmInstallationAccessLevel.FULL_CONTROL;
            },
            get accessLevelOption() {
                return findAccessLevelOption(this.accessLevel);
            },
            set accessLevelOption(option) {
                const newAccessLevel = option.accessLevel;
                const adminIsDowngradingSelf =
                    this.fullControl &&
                    userGroup.users.some(user => user.idUser === self.UserService.user.id) &&
                    newAccessLevel !== self.vrmInstallationAccessLevel.FULL_CONTROL;

                if (adminIsDowngradingSelf) {
                    const currentAccessLevel = this.accessLevel;
                    this.accessLevel = newAccessLevel;
                    self.$timeout(() => {
                        this.accessLevel = currentAccessLevel;
                    });

                    self.VrmModalService.openConfirmModal(
                        self.$rootScope,
                        'change_group_access',
                        confirmed => {
                            if (confirmed) {
                                this.changeAccessLevel(newAccessLevel, true);
                            }
                        },
                        {
                            yesBtnClass: 'sg-red',
                            noTranslation: 'common.cancel',
                        },
                    );
                } else {
                    this.changeAccessLevel(newAccessLevel);
                }
            },
            async changeAccessLevel(accessLevel, withReload = false) {
                await self.updateUserGroupAccessLevel(this.idUserGroup, accessLevel, withReload);
                this.accessLevel = accessLevel;
            },
        }));
        this.siteGroups = data.siteGroups.map(siteGroup => {
            if (!siteGroup.teams || !siteGroup.users) {
                return siteGroup;
            }
            const teamsWithMembers = siteGroup.teams.map(team => {
                return {
                    ...team,
                    id: team.idUserGroup,
                    isTeam: true,
                    members: siteGroup.users.filter(user =>
                        // check if the user is part of the team
                        user.teams.some(userTeam => userTeam.idUserGroup === team.idUserGroup),
                    ),
                };
            });

            // InstallationGroupService only returns site groups which the active user is either owner or admin
            const eligibleSiteGroups = this.InstallationGroupsService.groups.filter(
                group => group.idSiteGroup === siteGroup.idSiteGroup,
            );

            return {
                ...siteGroup,
                isOwnerOrAdmin: eligibleSiteGroups.length !== 0,
                membersAndTeams: siteGroup.users
                    .filter(user => user.directly_linked)
                    .map(user => ({ ...user, id: user.idUser }))
                    .concat(teamsWithMembers),
            };
        });
    }

    async updateUserAccessLevel(idUser, accessLevel) {
        try {
            await this.ApiService.call2(`/installations/${this.idSite}/user-rights`, {
                idUser: [idUser],
                accessLevel: [accessLevel],
            });
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('core.settings_saved'),
            });
            if (idUser === this.UserService.user.id) {
                await this.InstallationsService.reload();
                await this.loadSiteData();
            }
        } catch {
            await this.loadListData();
        }
    }

    async updateInviteAccessLevel(idInvite, accessLevel) {
        try {
            await this.ApiService.call2(`/installations/${this.idSite}/invite-rights`, {
                email: [idInvite],
                accessLevel: [accessLevel],
            });
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('core.settings_saved'),
            });
        } catch {
            await this.loadListData();
        }
    }

    async updateUserGroupAccessLevel(idUserGroup, accessLevel, withReload = false) {
        await this.UserGroupsService.setUserGroupAccessLevelAsSiteAdmin(
            this.idSite,
            idUserGroup,
            accessLevel,
        );
        if (withReload) {
            await this.InstallationsService.reload();
            await this.loadSiteData();
        }
        this.vrmNotificationsService.add({
            type: NOTIFICATION_TYPE_SUCCESS,
            message: this.$t('users.team_link_updated'),
        });
    }

    async unlinkUser(idUser) {
        const props = {
            item: this.$t('users.confirm_deletion_item_user'),
            message: this.$t('users.confirm_deletion_message_user'),
            action: this.$t('users.confirm_unlink_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            await this.ApiService.call2(`/installations/${this.idSite}/unlink-user`, {
                idUser: idUser,
            });
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('users.user_removed_message'),
            });
            const index = this.users.findIndex(user => user.idUser === idUser);
            this.users.splice(index, 1);
            if (this.localUserId === idUser) {
                this.$router.replace({ name: 'base.installation-overview' });
                this.$rootScope.$emit('installation:delete');
            }
        }
    }

    async removeInvite(idInvite) {
        const props = {
            item: this.$t('users.confirm_deletion_item_pending_invitation'),
            message: this.$t('users.confirm_deletion_message_pending_invitation'),
            action: this.$t('users.confirm_remove_invite_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            await this.ApiService.call2(
                `/installations/${this.idSite}/invite-delete`,
                { email: idInvite },
                { method: 'DELETE' },
            );
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('users.invite_removed_message'),
            });
            const index = this.invitedUsers.findIndex(invite => invite.email === idInvite);
            this.invitedUsers.splice(index, 1);
        }
    }

    async patchInstallationGroup(idSiteGroup, payload) {
        await this.ApiService.call2(`/installations/groups/${idSiteGroup}/patch`, payload);
        this.vrmNotificationsService.add({
            type: NOTIFICATION_TYPE_SUCCESS,
            message: this.$t('core.settings_saved'),
        });
    }

    async unlinkInstallationGroup(idSiteGroup) {
        const props = {
            item: this.$t('users.confirm_deletion_item_installation_group'),
            message: this.$t('users.confirm_deletion_message_installation_group'),
            action: this.$t('users.confirm_unlink_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            await this.patchInstallationGroup(idSiteGroup, {
                sites: [{ idSite: this.idSite, linked: false }],
            });
            const index = this.siteGroups.findIndex(
                siteGroup => siteGroup.idSiteGroup === idSiteGroup,
            );
            if (index >= 0) {
                this.siteGroups.splice(index, 1);
            }
        }
    }

    async unlinkTeam(idUserGroup) {
        const props = {
            item: this.$t('users.confirm_deletion_item_team'),
            message: this.$t('users.confirm_deletion_message_team'),
            action: this.$t('users.confirm_unlink_action'),
        };

        const confirmed = await this.VrmModalService.openVueModal(ConfirmDeletionModal, props);

        if (confirmed) {
            await this.UserGroupsService.unlinkSite(idUserGroup, this.idSite);
            this.vrmNotificationsService.add({
                type: NOTIFICATION_TYPE_SUCCESS,
                message: this.$t('users.team_unlinked_message'),
            });
            const index = this.userGroups.findIndex(
                userGroup => userGroup.idUserGroup === idUserGroup,
            );
            this.userGroups.splice(index, 1);
        }
    }

    async answerPending(request, approved) {
        await this.ApiService.call2(`/installations/${this.idSite}/update-add-request`, {
            idUser: request.idUser,
            accessLevel: request.accessLevel ? request.accessLevel : 0,
            approved,
        });
        this.vrmNotificationsService.add({
            type: NOTIFICATION_TYPE_SUCCESS,
            message: this.$t(approved ? 'users.request_approved' : 'users.request_denied'),
        });
        // Filter out requests matching the invite
        this.pendingUsers = this.pendingUsers.filter(
            invite => invite.idUser !== request.idUser || invite.idSite !== request.idSite,
        );
        await this.loadListData();
    }

    showRoleGuideModal() {
        this.VrmModalService.openVueModal(UserRolesGuideModal);
    }

    async showLinkUserGroupsPopup() {
        await this.VrmModalService.openVueModal(InstallationLinkUserGroupsModal, {
            userAccessLevelOptions: accessLevelDropdownOptionsFiltered(this.installation),
            idSite: this.idSite,
        });
        this.loadListData();
    }

    async showInviteUserPopup() {
        await this.VrmModalService.openVueModal(InstallationUserInviteModal, {
            userAccessLevelOptions: accessLevelDropdownOptionsFiltered(this.installation),
            idSite: this.idSite,
        });
        this.loadListData();
    }
}
</script>
